import React, { useState, useEffect } from 'react';
import { eventsAPI } from '../../utils/api';

// ConnectionFunnelPage — aggregate view of where connections drop off.
// Pivots cc_connection_dialog_step_completed and cancel/dismissed events
// into a per-step funnel: of all dialogs that started, how many reached
// each step and how many bailed there. Filterable by device_type
// (miracast/cc/dlna) and app_version.
//
// Pairs with the per-dialog drawer ladder on LiveEventsPage. That drawer
// answers "where did THIS Miracast session stall"; this page answers
// "where do MOST Miracast sessions stall?".

const DEVICE_TYPES = [
    { value: '',         label: 'All' },
    { value: 'miracast', label: 'Miracast' },
    { value: 'dlna',     label: 'DLNA' },
    { value: 'cc',       label: 'Chromecast' },
];

export default function ConnectionFunnelPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(30);
    const [deviceType, setDeviceType] = useState('miracast');

    useEffect(() => {
        setLoading(true);
        eventsAPI.getConnectionFunnel({ days, device_type: deviceType || undefined })
            .then(r => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [days, deviceType]);

    if (loading && !data) return <div className="p-8 text-gray-400">Loading funnel…</div>;
    if (!data) return <div className="p-8 text-red-500">Failed to load funnel.</div>;

    const total = data.total_dialogs || 0;
    const steps = data.steps || [];
    const final = data.final_states || {};

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Connection Funnel</h2>
                <div className="flex gap-3 items-center">
                    <select
                        value={deviceType}
                        onChange={e => setDeviceType(e.target.value)}
                        className="border rounded px-2 py-1 text-sm">
                        {DEVICE_TYPES.map(d => (
                            <option key={d.value} value={d.value}>{d.label}</option>
                        ))}
                    </select>
                    <select
                        value={days}
                        onChange={e => setDays(parseInt(e.target.value, 10))}
                        className="border rounded px-2 py-1 text-sm">
                        <option value={7}>Last 7 days</option>
                        <option value={30}>Last 30 days</option>
                        <option value={90}>Last 90 days</option>
                    </select>
                </div>
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <Kpi title="Total dialogs" value={total} />
                <Kpi
                    title="Succeeded"
                    value={final.success || 0}
                    pct={total ? ((final.success || 0) / total * 100).toFixed(1) + '%' : '—'}
                    tone="green"
                />
                <Kpi
                    title="Cancelled"
                    value={final.cancelled || 0}
                    pct={total ? ((final.cancelled || 0) / total * 100).toFixed(1) + '%' : '—'}
                    tone="yellow"
                />
                <Kpi
                    title="Failed"
                    value={final.failed || 0}
                    pct={total ? ((final.failed || 0) / total * 100).toFixed(1) + '%' : '—'}
                    tone="red"
                />
            </div>

            {/* Funnel — horizontal bar per step */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Funnel — {deviceType || 'all'} ({total} dialogs)
                </h3>

                {steps.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">
                        No step events recorded for this filter.
                    </p>
                ) : (
                    <div className="space-y-2">
                        {steps.map((s, i) => {
                            const pctReached = total ? (s.reached / total * 100) : 0;
                            const pctExited  = total ? (s.exited_here / total * 100) : 0;
                            const successRatePct = s.success_rate != null
                                ? `${(s.success_rate * 100).toFixed(0)}% advance to next step`
                                : 'final step';
                            const barColor = s.success_rate == null
                                ? 'bg-blue-500'
                                : s.success_rate >= 0.9 ? 'bg-green-500'
                                : s.success_rate >= 0.7 ? 'bg-yellow-500'
                                : 'bg-red-500';
                            return (
                                <div key={i}>
                                    <div className="flex items-baseline justify-between text-sm mb-1">
                                        <span className="font-mono font-medium text-gray-900">
                                            {i + 1}. {s.step_name}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                            <strong className="text-gray-900">{s.reached}</strong>/{total}{' '}
                                            ({pctReached.toFixed(1)}%) reached ·{' '}
                                            avg {s.avg_duration_ms || 0}ms ·{' '}
                                            <span className={barColor.replace('bg-', 'text-')}>{successRatePct}</span>
                                        </span>
                                    </div>
                                    <div className="relative h-6 bg-gray-100 rounded overflow-hidden">
                                        <div
                                            className={`absolute left-0 top-0 h-full ${barColor} transition-all`}
                                            style={{ width: `${pctReached}%` }}
                                        />
                                        {s.exited_here > 0 && (
                                            <div
                                                className="absolute top-0 h-full bg-red-700/40 border-l border-red-900"
                                                style={{
                                                    right: `${100 - pctReached}%`,
                                                    width: `${pctExited}%`,
                                                    marginRight: 0
                                                }}
                                                title={`${s.exited_here} dialogs cancelled here`}
                                            />
                                        )}
                                    </div>
                                    {s.exited_here > 0 && (
                                        <div className="text-[10px] text-red-700 mt-0.5">
                                            {s.exited_here} dialogs gave up at this step
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="text-xs text-gray-500 px-2">
                Window: last {days} days · device_type: {deviceType || 'all'} ·
                step ordering inferred from "reached" descending — see
                ConnectionStep.java for the canonical sequence
            </div>
        </div>
    );
}

function Kpi({ title, value, pct, tone }) {
    const toneClass = {
        green:  'text-green-700',
        yellow: 'text-yellow-700',
        red:    'text-red-700',
    }[tone] || 'text-gray-900';
    return (
        <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-xs uppercase tracking-wider text-gray-500">{title}</div>
            <div className={`text-2xl font-bold mt-1 ${toneClass}`}>{value}</div>
            {pct && <div className="text-xs text-gray-500 mt-1">{pct}</div>}
        </div>
    );
}
